
import React, { useEffect, useState, useRef } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import {
  FaMoneyBillWave,
  FaStore,
  FaCheckCircle,
  FaTimesCircle,
  FaArrowLeft,
  FaWallet,
  FaHistory,
  FaUserTie,
} from "react-icons/fa";
import { Chart, registerables } from "chart.js";
import { getAgentEarnings } from "../api/api";
import "../styles/AgentEarnings.css";

Chart.register(...registerables);

const StatCard = ({ icon, label, value, color, sub }) => (
  <div className={`ae-stat-card ae-stat-card--${color}`}>
    <div className="ae-stat-icon">{icon}</div>
    <div className="ae-stat-body">
      <p className="ae-stat-label">{label}</p>
      <p className="ae-stat-value">{value}</p>
      {sub && <p className="ae-stat-sub">{sub}</p>}
    </div>
  </div>
);

const CHART_COLORS = {
  purple: "#534AB7",
  green: "#1D9E75",
  red: "#D85A30",
  amber: "#BA7517",
};

function useDarkMode() {
  const [dark, setDark] = useState(
    window.matchMedia("(prefers-color-scheme: dark)").matches
  );
  useEffect(() => {
    const mq = window.matchMedia("(prefers-color-scheme: dark)");
    const handler = (e) => setDark(e.matches);
    mq.addEventListener("change", handler);
    return () => mq.removeEventListener("change", handler);
  }, []);
  return dark;
}

function DonutChart({ data }) {
  const canvasRef = useRef(null);
  const chartRef = useRef(null);
  const isDark = useDarkMode();

  useEffect(() => {
    if (!canvasRef.current) return;
    if (chartRef.current) chartRef.current.destroy();

    chartRef.current = new Chart(canvasRef.current, {
      type: "doughnut",
      data: {
        labels: data.map((d) => d.name),
        datasets: [
          {
            data: data.map((d) => (d.value === 0 ? 0.001 : d.value)),
            backgroundColor: [
              CHART_COLORS.purple,
              CHART_COLORS.green,
              CHART_COLORS.red,
              CHART_COLORS.amber,
            ],
            borderWidth: 3,
            borderColor: isDark ? "#1a1a1a" : "#ffffff",
            hoverOffset: 7,
          },
        ],
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        cutout: "68%",
        plugins: {
          legend: { display: false },
          tooltip: {
            callbacks: {
              label: (ctx) =>
                ` ${ctx.label}: ${
                  ctx.parsed === 0.001 ? 0 : ctx.parsed
                }`,
            },
          },
        },
      },
    });

    return () => chartRef.current?.destroy();
  }, [data, isDark]);

  return (
    <div style={{ position: "relative", width: "100%", height: 220 }}>
      <canvas ref={canvasRef} />
    </div>
  );
}

function BarChart({ data }) {
  const canvasRef = useRef(null);
  const chartRef = useRef(null);
  const isDark = useDarkMode();

  useEffect(() => {
    if (!canvasRef.current) return;
    if (chartRef.current) chartRef.current.destroy();

    const gridColor = isDark ? "rgba(255,255,255,0.07)" : "rgba(0,0,0,0.06)";
    const textColor = isDark ? "#a8a09a" : "#888780";

    chartRef.current = new Chart(canvasRef.current, {
      type: "bar",
      data: {
        labels: data.map((d) => d.name),
        datasets: [
          {
            data: data.map((d) => d.value),
            backgroundColor: [
              CHART_COLORS.purple,
              CHART_COLORS.green,
              CHART_COLORS.red,
            ],
            borderRadius: 6,
            borderSkipped: false,
          },
        ],
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
          legend: { display: false },
          tooltip: {
            callbacks: {
              label: (ctx) => ` Rs. ${ctx.parsed.y.toLocaleString()}`,
            },
          },
        },
        scales: {
          x: {
            grid: { display: false },
            border: { display: false },
            ticks: { color: textColor },
          },
          y: {
            grid: { color: gridColor, drawTicks: false },
            border: { display: false, dash: [4, 4] },
            ticks: {
              color: textColor,
              callback: (v) =>
                v >= 1000 ? `${v / 1000}k` : v,
            },
          },
        },
      },
    });

    return () => chartRef.current?.destroy();
  }, [data, isDark]);

  return (
    <div style={{ position: "relative", width: "100%", height: 220 }}>
      <canvas ref={canvasRef} />
    </div>
  );
}

function PaymentHistoryChart({ historyData }) {
  const canvasRef = useRef(null);
  const chartRef = useRef(null);
  const isDark = useDarkMode();

  useEffect(() => {
    if (!canvasRef.current || !historyData.length) return;
    if (chartRef.current) chartRef.current.destroy();

    const gridColor = isDark ? "rgba(255,255,255,0.07)" : "rgba(0,0,0,0.06)";
    const textColor = isDark ? "#a8a09a" : "#888780";

    chartRef.current = new Chart(canvasRef.current, {
      type: "bar",
      data: {
        labels: historyData.map((p) => p.label),
        datasets: [
          {
            label: "Total amount",
            data: historyData.map((p) => p.total),
            backgroundColor: CHART_COLORS.purple,
            borderRadius: 5,
            borderSkipped: false,
          },
          {
            label: "Drawable amount",
            data: historyData.map((p) => p.drawable),
            backgroundColor: CHART_COLORS.green,
            borderRadius: 5,
            borderSkipped: false,
          },
        ],
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
          legend: { display: false },
          tooltip: {
            callbacks: {
              label: (ctx) =>
                ` ${ctx.dataset.label}: Rs. ${ctx.parsed.y.toLocaleString()}`,
            },
          },
        },
        scales: {
          x: {
            grid: { display: false },
            border: { display: false },
            ticks: {
              color: textColor,
              autoSkip: false,
              maxRotation: 45,
            },
          },
          y: {
            grid: { color: gridColor, drawTicks: false },
            border: { display: false, dash: [4, 4] },
            ticks: {
              color: textColor,
              callback: (v) =>
                v >= 1000 ? `Rs. ${v / 1000}k` : `Rs. ${v}`,
            },
          },
        },
        barPercentage: 0.5,
        categoryPercentage: 0.6,
      },
    });

    return () => chartRef.current?.destroy();
  }, [historyData, isDark]);

  return (
    <div style={{ position: "relative", width: "100%", height: 260 }}>
      <canvas ref={canvasRef} />
    </div>
  );
}

export default function AgentEarnings({ isSidebarOpen }) {
  const location = useLocation();
  const navigate = useNavigate();
  const agentData = location.state?.user || {};
  const cnic = agentData?.cnic;

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!cnic) {
      setError("No agent selected.");
      setLoading(false);
      return;
    }
    const fetchData = async () => {
      try {
        const res = await getAgentEarnings(cnic);
        if (res?.isSuccess) {
          setData(res.data);
        } else {
          setError(res?.message || "Failed to load earnings.");
        }
      } catch (err) {
        setError("Error loading agent earnings.");
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [cnic]);

  if (loading) {
    return (
      <div className={`ae-container ${isSidebarOpen ? "shifted" : ""}`}>
        <p className="loader">Loading agent earnings...</p>
      </div>
    );
  }

  if (error || !data) {
    return (
      <div className={`ae-container ${isSidebarOpen ? "shifted" : ""}`}>
        <p style={{ color: "red" }}>{error || "No data available."}</p>
      </div>
    );
  }

  const { agent, earnings, paymentHistory } = data;

  const merchantBreakdown = [
    { name: "Active", value: earnings.activeMerchants },
    { name: "Inactive", value: earnings.inactiveMerchants },
    { name: "Paid", value: earnings.paidMerchants },
    { name: "Unpaid", value: earnings.unpaidMerchants },
  ];

  const amountBreakdown = [
    { name: "Total Amount", value: earnings.totalAmount },
    { name: "Paid Amount", value: earnings.paidAmount },
    { name: "Pending Amount", value: earnings.unpaidAmount },
    { name: "Inactive Amt", value: earnings.inactiveAmount },
  ];

  const historyChartData = [...paymentHistory]
    .slice(0, 6)
    .reverse()
    .map((p) => ({
      label: new Date(p.paidAt).toLocaleDateString("en-PK", {
        day: "2-digit",
        month: "short",
      }),
      drawable: p.drawableAmount,
      total: p.totalAmount,
    }));

  const LEGEND_ITEMS = [
    { color: CHART_COLORS.purple, label: "Active" },
    { color: CHART_COLORS.green, label: "Inactive" },
    { color: CHART_COLORS.red, label: "Paid" },
    { color: CHART_COLORS.amber, label: "Unpaid" },
  ];

  const AMOUNT_LEGEND = [
    { color: CHART_COLORS.purple, label: "Total" },
    { color: CHART_COLORS.green, label: "Drawable" },
    { color: CHART_COLORS.red, label: "Inactive" },
  ];

  const HISTORY_LEGEND = [
    { color: CHART_COLORS.purple, label: "Total amount" },
    { color: CHART_COLORS.green, label: "Drawable amount" },
  ];

  return (
    <div className={`ae-container ${isSidebarOpen ? "shifted" : ""}`}>
      {/* Header */}
      <div className="ae-header">
        <div className="ae-agent-info">
          <FaUserTie className="ae-agent-avatar" />
          <div>
            <h2>{agent.name}</h2>
            <p>
              CNIC: {agent.cnic}&nbsp;|&nbsp;Phone: {agent.phoneNumber}
            </p>
            {agent.iban && (
              <p>
                IBAN: {agent.iban}&nbsp;|&nbsp;{agent.accountTitle}
              </p>
            )}
          </div>
          <span
            className={`ae-payment-badge ${
              agent.isPaymentRequested ? "requested" : ""
            }`}
          >
            {agent.isPaymentRequested
              ? "⚡ Payment Requested"
              : "No Pending Request"}
          </span>
        </div>
      </div>

      {/* Stat Cards */}
      <div className="ae-stats-grid">
        <StatCard
          icon={<FaStore />}
          label="Total Merchants"
          value={earnings.totalMerchants}
          color="purple"
          sub={`Rs. ${earnings.totalAmount.toLocaleString()} total`}
        />
        <StatCard
          icon={<FaCheckCircle />}
          label="Active Merchants"
          value={earnings.activeMerchants}
          color="green"
          
        />
        <StatCard
          icon={<FaTimesCircle />}
          label="Inactive Merchants"
          value={earnings.inactiveMerchants}
          color="red"
         
        />
        <StatCard
          icon={<FaWallet />}
          label="Unpaid Merchants"
          value={earnings.unpaidMerchants}
          color="amber"
          sub={`Rs. ${earnings.unpaidMerchants * 100} pending`}
        />
        <StatCard
          icon={<FaMoneyBillWave />}
          label="Pending Amount"
          value={`Rs. ${earnings.unpaidAmount.toLocaleString()}`}
          color="teal"
         
        />
        <StatCard
          icon={<FaMoneyBillWave />}
          label="Paid Amount"
          value={`Rs. ${earnings.paidAmount.toLocaleString()}`}
          color="teal"
          
        />
        <StatCard
          icon={<FaMoneyBillWave />}
          label="InActive Amount"
          value={`Rs. ${earnings.inactiveAmount.toLocaleString()}`}
          color="teal"
          
        />
        <StatCard
          icon={<FaHistory />}
          label="Payment Transactions"
          value={paymentHistory.length}
          color="blue"
          sub="Total payments done"
        />
      </div>

      {/* Charts Row */}
      <div className="ae-charts-row">
        {/* Merchant Breakdown Donut */}
        <div className="ae-chart-card">
          <div className="ae-chart-header">
            <div>
              <p className="ae-chart-eyebrow">Overview</p>
              <h3 className="ae-chart-title">Merchant Breakdown</h3>
            </div>
          </div>
          <div className="ae-chart-legend">
            {LEGEND_ITEMS.map((item) => (
              <span key={item.label} className="ae-legend-item">
                <span
                  className="ae-legend-dot"
                  style={{ background: item.color }}
                />
                {item.label}
              </span>
            ))}
          </div>
          <DonutChart data={merchantBreakdown} />
        </div>

        {/* Amount Breakdown Bar */}
        <div className="ae-chart-card">
          <div className="ae-chart-header">
            <div>
              <p className="ae-chart-eyebrow">Financials</p>
              <h3 className="ae-chart-title">Amount Breakdown (Rs)</h3>
            </div>
          </div>
          <div className="ae-chart-legend">
            {AMOUNT_LEGEND.map((item) => (
              <span key={item.label} className="ae-legend-item">
                <span
                  className="ae-legend-dot"
                  style={{ background: item.color }}
                />
                {item.label}
              </span>
            ))}
          </div>
          <BarChart data={amountBreakdown} />
        </div>

        {/* Payment History Chart */}
        {historyChartData.length > 0 && (
          <div className="ae-chart-card ae-chart-card--wide">
            <div className="ae-chart-header">
              <div>
                <p className="ae-chart-eyebrow">Trend</p>
                <h3 className="ae-chart-title">Payment History (Last 6)</h3>
              </div>
            </div>
            <div className="ae-chart-legend">
              {HISTORY_LEGEND.map((item) => (
                <span key={item.label} className="ae-legend-item">
                  <span
                    className="ae-legend-dot"
                    style={{ background: item.color }}
                  />
                  {item.label}
                </span>
              ))}
            </div>
            <PaymentHistoryChart historyData={historyChartData} />
          </div>
        )}
      </div>

      {/* Payment History Table */}
      <div className="ae-history-section">
        <h3 className="ae-section-title">
          <FaHistory /> Payment History
        </h3>
        {paymentHistory.length === 0 ? (
          <p className="ae-no-history">No payment history yet.</p>
        ) : (
          <div className="ae-table-wrapper">
            <table className="ae-table">
              <thead>
                <tr>
                  <th>#</th>
                  <th>Date & Time</th>
                  <th>Merchants Paid</th>
                  <th>Total Merchants</th>
                  <th>Active</th>
                  <th>Inactive</th>
                  <th>Total Amount</th>
                  <th>Drawable Amount</th>
                  <th>Inactive Amount</th>
                  <th>Paid By</th>
                </tr>
              </thead>
              <tbody>
                {paymentHistory.map((p, i) => (
                  <tr key={p._id}>
                    <td>{i + 1}</td>
                    <td>
                      {new Date(p.paidAt).toLocaleDateString("en-PK", {
                        day: "2-digit",
                        month: "short",
                        year: "numeric",
                      })}{" "}
                      {new Date(p.paidAt).toLocaleTimeString("en-PK", {
                        hour: "2-digit",
                        minute: "2-digit",
                      })}
                    </td>
                    <td>
                      <span className="ae-badge ae-badge--green">
                        {p.merchantsPaidCount}
                      </span>
                    </td>
                    <td>{p.totalMerchantsAtPayment}</td>
                    <td>{p.activeMerchantsAtPayment}</td>
                    <td>{p.inactiveMerchantsAtPayment}</td>
                    <td className="ae-amount">
                      Rs. {p.totalAmount.toLocaleString()}
                    </td>
                    <td className="ae-amount ae-amount--green">
                      Rs. {p.drawableAmount.toLocaleString()}
                    </td>
                    <td className="ae-amount ae-amount--red">
                      Rs. {p.inactiveAmount.toLocaleString()}
                    </td>
                    <td>
                      <span className="ae-badge ae-badge--purple">
                        {p.paidBy || "—"}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
